// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XPopup from "@gcg/App/Components/XPopup/XPopup";
import ProductDescription, { ProductDescriptionStyles } from "@gcg/Overrides/Components/ProductDescription";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@gcg/Overrides/Components/ProductPartNumbers";
import { cartLineCardExpandedStyles } from "@gcg/Overrides/Widgets/Cart/CartLineCardExpanded";
import CartLineNotes from "@gcg/Overrides/Widgets/Cart/CartLineNotes";
import { screen } from "@gcg/XDL/Helpers";
import Wishlist from "@gcg/XDL/Icons/Wishlist";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import setRequisitionIsExpanded from "@insite/client-framework/Store/Pages/Requisitions/Handlers/SetRequisitionIsExpanded";
import setRequisitionIsSelected from "@insite/client-framework/Store/Pages/Requisitions/Handlers/SetRequisitionIsSelected";
import translate from "@insite/client-framework/Translate";
import { RequisitionModel } from "@insite/client-framework/Types/ApiModels";
import ProductAvailability from "@insite/content-library/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import RequisitionsRequisitionLinesTable from "@insite/content-library/Widgets/Requisitions/RequisitionsRequisitionLinesTable";
import { AccordionPresentationProps } from "@insite/mobius/Accordion";
import Button from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps, CheckboxProps } from "@insite/mobius/Checkbox";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    requisition: RequisitionModel;
    availability?: any;
    isInStock?: boolean;
    isPartial?: boolean;
    isBackOrder?: boolean;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    isSelected: !!state.pages.requisitions.selectedRequisitionIds.find(o => o === ownProps.requisition.id),
    isExpanded: !!state.pages.requisitions.expandedRequisitionIds.find(o => o === ownProps.requisition.id),
    productSettings: getSettingsCollection(state).productSettings,
    session: state.context.session,
    wishListSettings: getSettingsCollection(state).wishListSettings,
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
});

const mapDispatchToProps = {
    setRequisitionIsSelected,
    setRequisitionIsExpanded,
    addToWishList,
    setAddToListModalIsOpen,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RequisitionsProductListLineStyles {
    mainContainer?: GridContainerProps;
    imageGridItem?: GridItemProps;
    selectedCheckbox?: CheckboxPresentationProps;
    imageWrapper?: InjectableCss;
    productImageStyles?: ProductImageStyles;
    infoGridItem?: GridItemProps;
    infoContainer?: GridContainerProps;
    descriptionGridItem?: GridItemProps;
    productBrandStyles?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    partNumbersGridItem?: GridItemProps;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    priceGridItem?: GridItemProps;
    productPriceStyles?: ProductPriceStyles;
    quantityGridItem?: GridItemProps;
    quantityHeadingAndText?: SmallHeadingAndTextStyles;
    subtotalGridItem?: GridItemProps;
    subtotalHeadingAndText?: SmallHeadingAndTextStyles;
    requisitionLinesGridItem?: GridItemProps;
    requisitionLinesAccordion?: AccordionPresentationProps;
    viewRequisitionLink?: LinkPresentationProps;
    smallHeadingText?: SmallHeadingAndTextStyles;
    certificationsStyles?: InjectableCss;
    subtotalWithoutVatHeadingAndText?: SmallHeadingAndTextStyles;
}

export const requisitionsProductListLineStyles: RequisitionsProductListLineStyles = {
    mainContainer: {
        gap: 10,
        css: css`
            padding: 20px 0;
            border-top: 1px solid ${getColor("common.border")};
        `,
    },
    imageGridItem: {
        width: [1, 1, 1, 1, 1],
    },
    selectedCheckbox: {
        css: css`
            z-index: 5;
            position: absolute;
            top: 10px;
            left: 10px;
        `,
    },
    imageWrapper: {
        css: css`
            width: 100%;
            min-width: 0;
            flex-direction: column;
        `,
    },
    viewRequisitionLink: {
        color: "text.main",
        typographyProps: {
            size: "14px",
            lineHeight: "10px",
        },
        icon: {
            iconProps: {
                src: Wishlist,
                size: 13,
                color: "primary.main",
            },
            position: "left",
        },
        css: css`
            margin-top: 0;
            margin-bottom: 10px;
        `,
    },
    infoGridItem: {
        width: 12,
    },
    infoContainer: {
        gap: 10,
    },
    descriptionGridItem: {
        width: [12, 12, 5, 6, 6],
        printWidth: 6,
        css: css`
            flex-direction: column;
        `,
    },
    partNumbersGridItem: { width: 12 },
    productPriceStyles: {
        priceLabelText: {
            variant: "p",
            transform: "uppercase",
            size: 10,
            weight: "bold",
            css: css`
                margin-bottom: 0.5rem;
            `,
        },
    },
    priceGridItem: { width: [4, 4, 2, 2, 2] },
    quantityGridItem: { width: [4, 4, 2, 2, 2] },
    quantityHeadingAndText: {
        heading: {
            size: 10,
            weight: "bold",
            css: css`
                margin-bottom: 0.5rem;
            `,
        },
    },
    subtotalGridItem: { width: [4, 4, 2, 2, 2] },
    subtotalHeadingAndText: {
        heading: {
            size: 10,
            weight: "bold",
            css: css`
                margin-bottom: 0.5rem;
            `,
        },
        text: {
            weight: "bold",
        },
    },
    requisitionLinesGridItem: {
        width: 12,
    },
    requisitionLinesAccordion: {
        css: css`
            width: 100%;
        `,
    },
    smallHeadingText: {
        heading: {
            css: css`
                display: none;
                @media (max-width: 800px) {
                    display: block;
                    font-size: 16px;
                }
            `,
        },
    },
    certificationsStyles: {
        css: css`
            ${cartLineCardExpandedStyles.certificationsStyles?.css};
            ${screen("gt", "xs")`
                margin-left: 15px;
            `}
        `,
    },
};

const styles = requisitionsProductListLineStyles;

const RequisitionsProductListLine = ({
    requisition,
    isSelected,
    isExpanded,
    productSettings,
    setRequisitionIsSelected,
    setRequisitionIsExpanded,
    session,
    wishListSettings,
    addToWishList,
    setAddToListModalIsOpen,
    enableVat,
    vatPriceDisplay,
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    const [eisCertificate, setEisCertificate] = React.useState(
        requisition.properties["eis/GCGCertification"] === "true",
    );
    const [mfrCertificate, setMfrCertificate] = React.useState(requisition.properties["mfrCertification"] === "true");
    const selectChangeHandler: CheckboxProps["onChange"] = (_, value) => {
        setRequisitionIsSelected({ requisitionId: requisition.id, isSelected: value });
    };

    const togglePanelHandler = () => {
        setRequisitionIsExpanded({ requisitionId: requisition.id, isExpanded: !isExpanded });
    };

    const addToListClickHandler = () => {
        if (!wishListSettings) {
            return;
        }

        const productInfo = {
            productId: requisition.productId!,
            qtyOrdered: requisition.qtyOrdered!,
            unitOfMeasure: requisition.unitOfMeasure,
        };
        if (!wishListSettings.allowMultipleWishLists) {
            addToWishList({
                productInfos: [productInfo],
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
            });
            return;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos: [productInfo] });
    };

    const handleModalClose = () => {
        setRequisitionIsExpanded({ requisitionId: requisition.id, isExpanded: !isExpanded });
    };

    const badges = requisition?.properties["badges"] ? JSON.parse(requisition?.properties["badges"]) : null;

    const firstSectionWidth = css`
        width: 60%;
    `;
    const notEditableFirstSectionWidth = css`
        width: 62%;
    `;
    const columnWidth = css`
        width: calc(100% / 3);
    `;

    const firstSectionStyles = css`
        display: flex;
        ${firstSectionWidth};

        &.not-editable {
            ${notEditableFirstSectionWidth};
        }

        @media (max-width: 800px) {
            width: 100% !important;
        }

        ${screen("lte", "xs")`flex-wrap: wrap;`}
    `;

    const columnStyles = css`
        ${columnWidth};
        text-align: center;

        &.not-editable.qty {
            text-align: left;
            padding-right: 11px;
        }
    `;
    return (
        <>
            <StyledWrapper
                {...cartLineCardExpandedStyles.container}
                data-test-selector={`cartline_expanded_${requisition.productId}_${requisition.unitOfMeasure}`}
            >
                <Checkbox {...styles.selectedCheckbox} checked={isSelected} onChange={selectChangeHandler}>
                    {translate("Select Line")}
                </Checkbox>
                <StyledWrapper {...cartLineCardExpandedStyles.infoContainer}>
                    <StyledWrapper
                        {...cartLineCardExpandedStyles.infoContainer}
                        css={firstSectionStyles}
                        className="not-editable"
                    >
                        <StyledWrapper {...cartLineCardExpandedStyles.productImageGridItem}>
                            <ProductImage
                                product={requisition}
                                extendedStyles={cartLineCardExpandedStyles.productImage}
                            />
                        </StyledWrapper>
                        <StyledWrapper {...cartLineCardExpandedStyles.cartLineInfoContainer}>
                            <StyledWrapper {...cartLineCardExpandedStyles.productDescriptionGridItem}>
                                <StyledWrapper
                                    css={css`
                                        width: 100%;
                                    `}
                                >
                                    {requisition.brand && (
                                        <ProductBrand
                                            brand={requisition.brand}
                                            extendedStyles={cartLineCardExpandedStyles.productBrand}
                                        />
                                    )}
                                </StyledWrapper>
                                <ProductDescription
                                    product={requisition}
                                    showElipsesOn="3"
                                    showElipses={true}
                                    extendedStyles={cartLineCardExpandedStyles.productDescription}
                                />
                            </StyledWrapper>
                            <StyledWrapper {...cartLineCardExpandedStyles.productInfoGridItem}>
                                <StyledWrapper {...cartLineCardExpandedStyles.productPartNumbersGridItem}>
                                    <ProductPartNumbers
                                        showLabel={true}
                                        showSpecNumber={true}
                                        showCustomerName={true}
                                        showManufacturerItem={true}
                                        showProductNumber={true}
                                        productNumber={requisition.erpNumber}
                                        customerProductNumber={requisition.customerName}
                                        manufacturerItem={requisition.manufacturerItem}
                                        specNumber={requisition.properties["specNumber"] || ""}
                                        extendedStyles={cartLineCardExpandedStyles.productPartNumbers}
                                    />
                                </StyledWrapper>
                                {!requisition.quoteRequired && (
                                    <StyledWrapper {...cartLineCardExpandedStyles.middleContentAreaGridItem}>
                                        <ProductAvailability
                                            productId={requisition.productId!}
                                            availability={requisition.availability!}
                                            unitOfMeasure={requisition.baseUnitOfMeasure}
                                            trackInventory={requisition.trackInventory}
                                            extendedStyles={cartLineCardExpandedStyles.productAvailability}
                                        />
                                        <StyledWrapper
                                            css={css`
                                                margin-top: 15px;
                                            `}
                                        >
                                            {badges &&
                                                badges.map(
                                                    (
                                                        badge: {
                                                            Image: string | undefined;
                                                            DisplayName: string;
                                                            Description: string;
                                                        },
                                                        index: any,
                                                    ) =>
                                                        badge.Image && (
                                                            <StyledWrapper
                                                                css={css`
                                                                    display: inline;
                                                                    margin-right: 5px;
                                                                `}
                                                                // eslint-disable-next-line react/no-array-index-key
                                                                key={`badge${index}`}
                                                            >
                                                                <XPopup
                                                                    type="tooltip"
                                                                    trigger={
                                                                        <StyledWrapper
                                                                            css={css`
                                                                                display: inline;
                                                                            `}
                                                                        >
                                                                            <LazyImage
                                                                                src={badge.Image}
                                                                                width="20px"
                                                                                height="20px"
                                                                            />
                                                                        </StyledWrapper>
                                                                    }
                                                                    position="top right"
                                                                >
                                                                    <StyledWrapper className="popup-content-wrapper">
                                                                        {/* eslint-disable react/no-danger */}
                                                                        <div
                                                                            dangerouslySetInnerHTML={{
                                                                                __html:
                                                                                    badge.DisplayName ===
                                                                                    "PROP_65_CANCER"
                                                                                        ? requisition.properties
                                                                                              .prop65Text ||
                                                                                          (siteMessage(
                                                                                              "Custom_Prop65DefaultText",
                                                                                          ) as string)
                                                                                        : badge.Description,
                                                                            }}
                                                                        ></div>
                                                                    </StyledWrapper>
                                                                </XPopup>
                                                            </StyledWrapper>
                                                        ),
                                                )}
                                        </StyledWrapper>
                                    </StyledWrapper>
                                )}
                            </StyledWrapper>
                        </StyledWrapper>
                        {/* <StyledWrapper {...styles.subtotalGridItem}>
                            {requisition.pricing && (
                                <>
                                    {enableVat && vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                        <SmallHeadingAndText
                                            heading={`${translate("Subtotal")} (${translate("Ex. VAT")})`}
                                            text={requisition.pricing.extendedUnitNetPriceDisplay}
                                            extendedStyles={styles.subtotalWithoutVatHeadingAndText}
                                        />
                                    )}
                                    <SmallHeadingAndText
                                        heading={
                                            !enableVat
                                                ? translate("Subtotal")
                                                : vatPriceDisplay !== "DisplayWithoutVat"
                                                ? `${translate("Subtotal")} (${translate("Inc. VAT")})`
                                                : `${translate("Subtotal")} (${translate("Ex. VAT")})`
                                        }
                                        text={
                                            enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                                ? requisition.pricing.extendedUnitRegularPriceWithVatDisplay
                                                : requisition.pricing.extendedUnitNetPriceDisplay
                                        }
                                        extendedStyles={styles.subtotalHeadingAndText}
                                        data-test-selector="subtotal"
                                    />
                                </>
                            )}
                        </StyledWrapper> */}
                    </StyledWrapper>
                    <StyledWrapper {...cartLineCardExpandedStyles.secondSection} className="not-editable">
                        <StyledWrapper
                            {...cartLineCardExpandedStyles.quantityAndExtendedUnitNetPriceContainer}
                            className="not-editable"
                        >
                            <StyledWrapper css={columnStyles} className="qty not-editable" {...styles.quantityGridItem}>
                                <SmallHeadingAndText
                                    heading={translate("Qty")}
                                    text=""
                                    extendedStyles={styles.smallHeadingText}
                                />
                                <SmallHeadingAndText heading="" text={requisition.qtyOrdered!} />
                            </StyledWrapper>
                            <StyledWrapper css={columnStyles} className="not-editable">
                                <SmallHeadingAndText
                                    heading={translate("Price")}
                                    text=""
                                    extendedStyles={styles.smallHeadingText}
                                />
                                <ProductPrice
                                    showLabel={false}
                                    product={requisition}
                                    showSavings={true}
                                    showSavingsAmount={productSettings.showSavingsAmount}
                                    showSavingsPercent={productSettings.showSavingsPercent}
                                />
                            </StyledWrapper>
                            <StyledWrapper css={columnStyles}>
                                {!requisition.quoteRequired && (
                                    <>
                                        <SmallHeadingAndText
                                            heading={translate("Total")}
                                            text=""
                                            extendedStyles={styles.smallHeadingText}
                                        />
                                        <Typography
                                            {...cartLineCardExpandedStyles.extendedUnitNetPriceText}
                                            data-test-selector="cartline_extendedUnitNetPrice"
                                        >
                                            {requisition.pricing?.extendedUnitNetPriceDisplay}
                                        </Typography>
                                    </>
                                )}
                            </StyledWrapper>
                        </StyledWrapper>
                        <StyledWrapper {...cartLineCardExpandedStyles.wishListAndCheckboxes}>
                            <StyledWrapper
                                css={css`
                                    display: flex;
                                    flex-direction: column;
                                `}
                            >
                                <Link {...cartLineCardExpandedStyles.addToListLink} onClick={addToListClickHandler}>
                                    {translate("Shopping List")}
                                </Link>
                                <Link {...styles.viewRequisitionLink} onClick={togglePanelHandler}>
                                    {translate("View Requisitions")}
                                </Link>
                            </StyledWrapper>
                            <StyledWrapper {...styles.certificationsStyles}>
                                <Checkbox
                                    checked={eisCertificate}
                                    disabled={true}
                                    data-test-selector={`line_eis_certificate_${requisition.id}`}
                                    {...cartLineCardExpandedStyles.certificationLabels}
                                >
                                    {siteMessage("Custom_requested_certification")}
                                </Checkbox>
                                <Checkbox
                                    checked={mfrCertificate}
                                    disabled={true}
                                    data-test-selector={`line_mfr_certificate_${requisition.id}`}
                                    {...cartLineCardExpandedStyles.certificationLabels}
                                >
                                    {translate("Request Mfr. Certification")}
                                </Checkbox>
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper
                    {...cartLineCardExpandedStyles.cartLineNotesGridItem}
                    className={!requisition.notes.length ? "no-value" : ""}
                >
                    <CartLineNotes editable={false} extendedStyles={cartLineCardExpandedStyles.cartLineNotes} />
                </StyledWrapper>
            </StyledWrapper>
            <Modal
                headline={translate("Viewing Requisition For:")}
                sizeVariant="large"
                isOpen={isExpanded}
                handleClose={handleModalClose}
            >
                <RequisitionsRequisitionLinesTable requisitionId={requisition.id} />
                <StyledWrapper className="modal-actions">
                    <Button variant="primary" onClick={handleModalClose}>
                        {translate("Continue")}
                    </Button>
                </StyledWrapper>
            </Modal>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(RequisitionsProductListLine);
